import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import { UserAgentValuesContext } from 'contexts';

const useStyles = makeStyles((theme) => {
  return {
    /* Styles applied to the root element. */
    children: {
      display: 'flex',
      maxWidth: ({ shouldUseButtonWidth }) =>
        shouldUseButtonWidth ? theme.spacing(4.5) : 'auto',
    },
  };
});

const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    display: 'flex',
    alignItems: 'center',
    minHeight: theme.spacing(5.75),
    margin: `${theme.spacing(0.75)} !important`,
    padding: theme.spacing(1.5),

    ...theme.typography.body2,
    fontSize: theme.typography.pxToRem(14),

    backgroundColor: '#394551',
  },
}));

const IconTooltip = ({
  className,
  children,
  title,
  showTooltipOnMobileInteraction = false,
  shouldUseButtonWidth = false,
  id,
  disabled,
  passDownProps,
  onOpen = () => {},
  onClose = () => {},
  ...props
}) => {
  const classes = useStyles({ shouldUseButtonWidth });
  const { userAgent } = useContext(UserAgentValuesContext);

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  let child = React.Children.only(children);
  let childAriaLabel = '';
  let childAriaLabelledBy = '';
  if (React.isValidElement(child)) {
    childAriaLabel = child.props['aria-label'] || '';
    childAriaLabelledBy = child.props['aria-labelledby'] || '';
  }
  const hasAriaLabel = childAriaLabel.length > 0;
  const hasAriaLabelledBy = childAriaLabelledBy.length > 0;

  // Use describeChild unless
  // 1. The button is disabled (tooltip won't be triggered)
  // 2. The button uses aria-labelledby.
  // 2. The aria-label matches the title.
  // Per MUI 5 accessibility guidance: https://mui.com/material-ui/react-tooltip/#accessibility
  const shouldUseDescribeChild =
    !disabled &&
    !hasAriaLabelledBy &&
    !(hasAriaLabel && childAriaLabel === title);

  return userAgent.isMobile && !showTooltipOnMobileInteraction ? (
    <div className={classes.children} {...props}>
      {children}
    </div>
  ) : (
    <StyledTooltip
      id={id}
      className={className}
      classes={{ tooltip: classes.root }}
      title={title}
      open={isTooltipOpen}
      onOpen={() => {
        onOpen();
        setIsTooltipOpen(true);
      }}
      onClose={() => {
        onClose();
        setIsTooltipOpen(false);
      }}
      onClick={() =>
        userAgent.isMobile &&
        showTooltipOnMobileInteraction &&
        setIsTooltipOpen(true)
      }
      {...(shouldUseDescribeChild ? { describeChild: true } : {})}
      // this fixes the tooltip flashing in Safari
      {...(userAgent.isSafari ? { TransitionProps: { timeout: 0 } } : {})}
      {...props}
    >
      {disabled ? (
        /* wrapping in a div to allow tooltips on disabled elements */
        // there is an issue with mui a11y for disabled elements
        // this is a temporary workaround
        // https://github.com/mui/material-ui/issues/33182
        <div className={classes.children} aria-label="">
          {children}
        </div>
      ) : (
        children
      )}
    </StyledTooltip>
  );
};

IconTooltip.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Override or extend the styles applied to the component.
   */
  classes: PropTypes.object,
  /**
   * @ignore
   */
  className: PropTypes.string,
  /**
   * The text to show on the tooltip
   */
  title: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.object.isRequired,
  ]),
};

export default IconTooltip;
